import { useMemo, useState } from "react";
import { useImmer } from "use-immer";

import { IgFilterAutocomplete } from "@intelligems/ig-theme/IgFilterAutocomplete";
import {
  AnalyticsMatchType,
  AnalyticsViewTypeFe,
  ChannelType,
  ConversionStageReachedType,
  CustomerType,
  CustomEventsFilterType,
  DeviceType,
  isAnalyticsChannel,
  LandingPageFilterItemType,
  UrlParamPayloadType,
  VisitorType,
} from "@intelligems/ig-types/schemas/analytics/base";
import { GenericExperienceFe } from "@intelligems/ig-types/fe-only/experiences";
import { officialCategoryName } from "@intelligems/ig-utils/common";
import { getTinybirdActiveAnalyticsFilters } from "@src/api/analytics/utils";
import { useConfiguration } from "@src/api/configurations/use-configuration";
import { usePatchExperienceAnalysis } from "@src/api/experience-analysis/patch-experience-analysis";
import type { AnalyticFiltersConfigType } from "@src/api/types/dashboards";
import { DashboardPageName } from "@src/api/types/dashboards";
import { ChannelChecklist } from "@src/components/AudienceFilters/Channel";
import { AnalyticsFiltersDrawer } from "@src/features/analytics/analyticsFilters/AnalyticsFiltersDrawer";
import { AnalyticsViewTypeFilter } from "@src/features/analytics/analyticsFilters/AnalyticsViewTypeFilter";
import { CountryCodesFilter } from "@src/features/analytics/analyticsFilters/CountryCodesFilter";
import { CustomEventsFilter } from "@src/features/analytics/analyticsFilters/CustomEventsFilter";
import { CustomerTypeFilter } from "@src/features/analytics/analyticsFilters/CustomerTypeFilter";
import { DeviceTypeFilter } from "@src/features/analytics/analyticsFilters/DeviceTypeFilter";
import { LandingPageMultiFilter } from "@src/features/analytics/analyticsFilters/LandingPageMultiFilter";
import { MinMaxRevenueFilter } from "@src/features/analytics/analyticsFilters/MinMaxRevenueFilter";
import { UserBehaviorFilter } from "@src/features/analytics/analyticsFilters/UserBehaviorFilter";
import {
  EMPTY_LANDING_PAGE_SINGLE_FILTER,
  getAllChannelTypes,
  getChannelTypeListCount,
  getCustomerTypeFilterDescription,
  isActiveLandingPageFilter,
  migrateLandingPageToFilters,
  padLandingPageFilters,
  useDialogFilterValidation,
} from "@src/features/analytics/analyticsFilters/utils";
import { VisitorTypeFilter } from "@src/features/analytics/analyticsFilters/VisitorTypeFilter";
import { useReadOnlyMode } from "@src/features/publicShareLinks/hooks/useReadOnlyMode";
import { useDispatch, useSelector } from "@src/store";
import { getAnalyticsProducts } from "@src/store/slices/analyticsProducts";
import {
  getHasAnyCheckoutBlocks,
  getHasAnyUpsellBlocks,
} from "@src/store/slices/checkoutBlocks/selectors";
import { setDashboardFilter } from "@src/store/slices/dashboardFilters";
import { closeDrawer } from "@src/store/slices/drawers";
import { getCustomMetrics } from "@src/store/slices/experienceMetrics/experienceMetrics";
import { selectedExperience } from "@src/store/slices/experiences";
import { getOfferProducts } from "@src/store/slices/offerProducts";
import { ProductPickerSelectedProduct } from "@src/store/slices/productPicker/types";
import {
  IExperienceDashboardFilters,
  initialDashboardFiltersState,
} from "@src/store/types/dashboardFilters";
import { mpClickAnalyticsFilterSaveButton } from "@src/utils/mixpanel";
import {
  calculateProductCategories,
  getAnalyticsPageType,
} from "@src/utils/mpUtils";

import { UrlParamFilter } from "./UrlParamFilter";
import { ZScoreFilter } from "./ZScoreFilter";

interface AnalyticsFilterDrawerWrapperProps {
  pageName?: DashboardPageName;
  currentExperience?: GenericExperienceFe;
  openFilters: boolean;
  renderLocation: "sitewide" | "experience";
  allCountryCodesData?: string[];
  shippingMethodsData?: string[];
}

export const AnalyticsFilterDrawerWrapper = ({
  currentExperience,
  pageName,
  openFilters,
  renderLocation,
  allCountryCodesData,
  shippingMethodsData,
}: AnalyticsFilterDrawerWrapperProps) => {
  const dispatch = useDispatch();
  const experience = useSelector(selectedExperience);
  const dashboardFilters = useSelector((state) => state.dashboardFilters);
  const mutateExperienceAnalysis = usePatchExperienceAnalysis(
    currentExperience?.id
  );
  const experienceCustomMetrics = useSelector(getCustomMetrics);
  const offerProducts = useSelector(getOfferProducts);
  const analyticsProducts = useSelector(getAnalyticsProducts);
  const maxZScoreConfig = useConfiguration("max_z_score");
  const { isReadOnly } = useReadOnlyMode();

  const hasUpsellBlock = useSelector(getHasAnyUpsellBlocks());
  const hasCheckoutBlocks = useSelector(getHasAnyCheckoutBlocks());
  const hasOnsiteUpsell =
    currentExperience?.testTypes.hasTestOnsiteUpsell ?? false;

  const hideTargetProductOption =
    !currentExperience?.testTypes.hasTestPricing &&
    !hasUpsellBlock &&
    !hasOnsiteUpsell &&
    analyticsProducts?.length === 0 &&
    offerProducts?.length === 0;

  const [dialogFilters, setDialogFilters] =
    useImmer<IExperienceDashboardFilters>({
      ...dashboardFilters,
      analyticsViewType:
        renderLocation === "sitewide"
          ? "sitewide_orders"
          : dashboardFilters.analyticsViewType,
      isZScoreEnabled: dashboardFilters.isZScoreEnabled ?? true,
      maxZScore: dashboardFilters.maxZScore ?? maxZScoreConfig!.value,
      customEvents: dashboardFilters.customEvents || [],
      countryCodes: dashboardFilters.countryCodes || [],
      countryCodesFilterType:
        dashboardFilters.countryCodesFilterType ?? "include",
      userBehavior: dashboardFilters.userBehavior ?? {
        viewedCheckout: null,
        addedToCart: null,
        viewedCollectionPage: null,
        viewedProductPage: null,
      },
      startTime: null,
      endTime: null,
      customerType: dashboardFilters.customerType ?? null,
      customEventsFilterType: dashboardFilters.customEventsFilterType || "all",
      deviceType: dashboardFilters.deviceType || "any",
      visitorType: dashboardFilters.visitorType || "any",
      channels:
        dashboardFilters.channels.length === 0
          ? getAllChannelTypes(true)
          : dashboardFilters.channels,
      reachedCheckoutStage: dashboardFilters.reachedCheckoutStage || "any",
      urlParam: {
        queryParam:
          dashboardFilters.urlParam?.queryParam !== null
            ? dashboardFilters.urlParam?.queryParam
            : null,
        queryParamValue:
          dashboardFilters.urlParam?.queryParamValue !== null
            ? dashboardFilters.urlParam?.queryParamValue
            : null,
        queryParamFilterType:
          dashboardFilters.urlParam?.queryParamFilterType !== null
            ? dashboardFilters.urlParam?.queryParamFilterType
            : null,
      },
      // The single-path `landingPage` filter is deprecated; the drawer edits the
      // multi-value array only. Lift any incoming single-path filter into it so a
      // filter that is still being applied stays visible and editable, and blank
      // the deprecated field so the two can't both apply after a save.
      landingPage: EMPTY_LANDING_PAGE_SINGLE_FILTER,
      landingPageFilters: padLandingPageFilters(
        migrateLandingPageToFilters(
          dashboardFilters.landingPage,
          dashboardFilters.landingPageFilters
        )
      ),
      landingPageFiltersOperator:
        dashboardFilters.landingPageFiltersOperator ?? "or",
      shippingMethods: dashboardFilters.shippingMethods ?? [],
      excludeFreeOrders: dashboardFilters.excludeFreeOrders ?? true,
      minNetRevenue: dashboardFilters.minNetRevenue ?? null,
      maxNetRevenue: dashboardFilters.maxNetRevenue ?? null,
      minShippingRevenue: dashboardFilters.minShippingRevenue ?? null,
      maxShippingRevenue: dashboardFilters.maxShippingRevenue ?? null,
      selectedAnalyticsProducts:
        dashboardFilters.selectedAnalyticsProducts ?? [],
    });

  const [selectedCustomEventIdentifiers, setSelectedCustomEventIdentifiers] =
    useImmer<string[]>(dashboardFilters.customEvents ?? []);
  const [isTestProductsDefaultChange, setIsTestProductsDefaultChange] =
    useState<boolean>(false);
  const validationErrors = useDialogFilterValidation(dialogFilters);
  const handleCustomEventSelect = (identifier: string) => {
    setSelectedCustomEventIdentifiers((draft) => {
      const index = draft.indexOf(identifier);
      if (index === -1) {
        draft.push(identifier); // Add if not already selected
      } else {
        draft.splice(index, 1); // Remove if already selected
      }
    });

    const updatedSelection = [...selectedCustomEventIdentifiers];
    const index = updatedSelection.indexOf(identifier);
    if (index === -1) {
      updatedSelection.push(identifier);
    } else {
      updatedSelection.splice(index, 1);
    }

    setDialogFilters((draft) => {
      draft.customEvents = updatedSelection;
      return draft;
    });
  };

  const onStringFilterChange = (newValue: string, filterKey: string) => {
    setDialogFilters((draft) => {
      (draft as Record<string, unknown>)[filterKey] = newValue;
      return draft;
    });
  };

  const onCustomerTypeChange = (value: CustomerType | null) => {
    setDialogFilters((draft) => {
      draft.customerType = value;
      return draft;
    });
  };
  const onNumberOrNullChange = (
    newValue: number | null,
    filterKey: keyof Pick<
      IExperienceDashboardFilters,
      | "minNetRevenue"
      | "maxNetRevenue"
      | "minShippingRevenue"
      | "maxShippingRevenue"
    >
  ) => {
    setDialogFilters((draft) => {
      draft[filterKey] = newValue;
      return draft;
    });
  };

  const onBooleanFilterChange = (
    checked: boolean,
    filterKey: "isZScoreEnabled" | "excludeFreeOrders"
  ) => {
    setDialogFilters((draft) => {
      draft[filterKey] = checked;
      return draft;
    });
  };

  const onSelectedAnalyticsProductsChange = (
    products: ProductPickerSelectedProduct[]
  ) => {
    setDialogFilters((draft) => {
      draft.selectedAnalyticsProducts = products;
      return draft;
    });
  };

  const onStringArrFilterChange = (
    newValues: string[],
    filterKey: "customEvents" | "countryCodes" | "shippingMethods"
  ) => {
    setDialogFilters((draft) => {
      draft[filterKey] = newValues;
      return draft;
    });
  };

  const onChannelClick = (channelType: ChannelType | null) => {
    const updatedChannels = dialogFilters.channels.filter(
      (channel) => !!channel
    );
    if (!channelType) {
      // handles all channels button
      setDialogFilters((draft) => {
        draft.channels =
          draft.channels.length === getChannelTypeListCount(true)
            ? []
            : getAllChannelTypes(true);
        return draft;
      });
    } else if (!isAnalyticsChannel(channelType)) {
      // The picker's options are shared with audience targeting, whose channel
      // vocabulary is wider. Anything analytics cannot bucket on is not a valid
      // analytics filter, so ignore it rather than write it into filter state.
      return;
    } else if (updatedChannels.includes(channelType)) {
      // handles removing channel when un selected
      setDialogFilters((draft) => {
        draft.channels = draft.channels.filter(
          (channel) => channel !== channelType
        );
        return draft;
      });
    } else {
      // handles adding unselected channels when selected
      setDialogFilters((draft) => {
        draft.channels = [...(updatedChannels || []), channelType];
      });
    }
  };

  const onUrlParamChange = (
    value: string | null,
    key: keyof UrlParamPayloadType
  ) => {
    setDialogFilters((draft) => {
      draft.urlParam ??= {
        queryParam: null,
        queryParamFilterType: null,
        queryParamValue: null,
      };

      if (key === "queryParamFilterType") {
        // The operator select is populated from FILTER_TYPE_OPTIONS, which is
        // exactly AnalyticsMatchList — the pipes have no `regex` branch, so a
        // wider UrlMatch here would let a value through that analytics-v2 400s.
        draft.urlParam.queryParamFilterType =
          value as AnalyticsMatchType | null;
      } else {
        // key is "queryParam" | "queryParamValue" here, both string | null, so
        // the union-key write resolves to string | null and needs no cast.
        draft.urlParam[key] = value;
      }

      return draft;
    });
  };

  const onLandingPageFiltersChange = (filters: LandingPageFilterItemType[]) => {
    setDialogFilters((draft) => {
      draft.landingPageFilters = filters;
    });
  };

  const filterButtons: AnalyticFiltersConfigType[] = useMemo(() => {
    return [
      {
        label: "Device Type",
        settings: [
          {
            key: "deviceType",
            description: "Filter by device type",
            component: (
              <DeviceTypeFilter
                onStringFilterChange={onStringFilterChange}
                deviceType={dialogFilters.deviceType}
              />
            ),
          },
        ],
        isSwitch: false,
        showOnPage: "all",
      },
      {
        label: "Customer Type",
        settings: [
          {
            key: "customerType",
            description: getCustomerTypeFilterDescription(currentExperience),
            component: (
              <CustomerTypeFilter
                customerType={dialogFilters.customerType}
                onCustomerTypeChange={onCustomerTypeChange}
              />
            ),
          },
        ],
        isSwitch: false,
        showOnPage: "all",
        config: renderLocation !== "sitewide",
      },
      {
        label: "Visitor Type",
        settings: [
          {
            key: "visitorType",
            description: "Filter by visitor type",
            component: (
              <VisitorTypeFilter
                onStringFilterChange={onStringFilterChange}
                visitorType={dialogFilters.visitorType}
              />
            ),
          },
        ],
        isSwitch: false,
        showOnPage: "all",
      },
      {
        label: "Source Channels & Sites",
        settings: [
          {
            key: "channelType",
            description:
              "Filter by one or more channel and traffic source type",
            component: (
              <ChannelChecklist
                onClick={onChannelClick}
                channels={dialogFilters.channels}
              />
            ),
          },
        ],
        isSwitch: false,
        showOnPage: "all",
      },
      {
        label: "URL Parameter",
        settings: [
          {
            key: "urlParam",
            description:
              "Filter to visitors from specific ad experiences or media",
            component: (
              <UrlParamFilter
                onUrlParamChange={onUrlParamChange}
                urlParam={dialogFilters.urlParam}
                onReset={() => {
                  setDialogFilters((draft) => {
                    draft.urlParam = {
                      queryParam: null,
                      queryParamValue: null,
                      queryParamFilterType: null,
                    };
                    return draft;
                  });
                }}
              />
            ),
          },
        ],
        isSwitch: false,
        showOnPage: "all",
        config: renderLocation !== "sitewide",
      },
      {
        label: "Landing Page",
        isSwitch: false,
        showOnPage: "all",
        settings: [
          {
            key: "landingPageFilters",
            description: "Filter to visitors by their landing page URL.",
            component: (
              <LandingPageMultiFilter
                filters={dialogFilters.landingPageFilters ?? []}
                onChange={onLandingPageFiltersChange}
              />
            ),
          },
        ],
      },
      {
        label: "User Behavior",
        settings: [
          {
            key: "userBehavior",
            description: "Filter by conversion stage and browsing activity",
            component: (
              <UserBehaviorFilter
                hasCheckoutBlocks={hasCheckoutBlocks}
                hasOnsiteUpsell={hasOnsiteUpsell}
                hasTestPostPurchase={
                  currentExperience?.testTypes.hasTestPostPurchase ?? false
                }
                onStringFilterChange={onStringFilterChange}
                conversionStageReached={
                  dialogFilters.reachedCheckoutStage ?? "any"
                }
                setDialogFilters={setDialogFilters}
                userBehavior={dialogFilters.userBehavior}
              />
            ),
          },
        ],
        isSwitch: false,
        showOnPage: "all",
        config: renderLocation !== "sitewide",
      },
      {
        label: "Custom Events",
        settings: [
          {
            key: "customEvents",
            component: (
              <CustomEventsFilter
                customEventsFilterType={
                  dialogFilters.customEventsFilterType ?? "all"
                }
                onChange={onStringFilterChange}
                selectedCustomEventIds={selectedCustomEventIdentifiers}
                onSelect={handleCustomEventSelect}
              />
            ),
          },
        ],
        isSwitch: false,
        config:
          currentExperience !== undefined && experienceCustomMetrics.length > 0,
        showOnPage: "all",
      },
      {
        label: "Filter Orders by Product",
        settings: [
          {
            key: "analyticsViewType",
            description:
              "Limit analytics to orders containing specific products",
            component: (
              <AnalyticsViewTypeFilter
                hideTargetProductOption={hideTargetProductOption}
                currentExperience={currentExperience}
                analyticsViewType={dialogFilters.analyticsViewType}
                onStringFilterChange={onStringFilterChange}
                setIsTestProductsDefaultChange={setIsTestProductsDefaultChange}
                isTestProductsDefaultChange={isTestProductsDefaultChange}
                selectedAnalyticsProducts={
                  dialogFilters.selectedAnalyticsProducts
                }
                onSelectedAnalyticsProductsChange={
                  onSelectedAnalyticsProductsChange
                }
              />
            ),
          },
        ],
        showOnPage: "all",
        hasDefault: true,
        tooltip:
          "Target products are automatically set to your price test or offer products. For other test types, you can manually define target products in the test setup.",
        config: !(hideTargetProductOption && isReadOnly),
      },
      {
        label: "Countries",
        settings: [
          {
            key: "countryCodes",
            description: "Filter visitors by country",
            component: (
              <CountryCodesFilter
                selectedValues={dialogFilters.countryCodes}
                countryCodesFilterType={
                  dialogFilters.countryCodesFilterType ?? "include"
                }
                allDataOptions={allCountryCodesData!}
                onFilterTypeChange={(v) =>
                  onStringFilterChange(v, "countryCodesFilterType")
                }
                onValuesChange={(v) =>
                  onStringArrFilterChange(v, "countryCodes")
                }
              />
            ),
          },
        ],
        isSwitch: false,
        showOnPage: "all",
        config:
          currentExperience &&
          allCountryCodesData &&
          allCountryCodesData?.length > 0,
      },
      {
        label: "Order Value",
        isSwitch: false,
        showOnPage: "all",
        settings: [
          {
            key: "minMaxNetRevenue",
            description:
              "Limit analytics to orders within a specific revenue range",
            component: (
              <MinMaxRevenueFilter
                minRevenue={dialogFilters.minNetRevenue}
                maxRevenue={dialogFilters.maxNetRevenue}
                onNumberOrNullChange={onNumberOrNullChange}
                maxKey={"maxNetRevenue"}
                minKey={"minNetRevenue"}
                minLabel={"Min order value"}
                maxLabel={"Max order value"}
                excludeFreeOrders={dialogFilters.excludeFreeOrders}
                onExcludeFreeOrdersChange={(value) =>
                  onBooleanFilterChange(value, "excludeFreeOrders")
                }
              />
            ),
          },
        ],
      },
      {
        label: "Min/Max Shipping Revenue",
        isSwitch: false,
        showOnPage: "all",
        config: experience.testTypes.hasTestShipping,
        settings: [
          {
            key: "minMaxNetShippingRevenue",
            description:
              "Limit analytics to orders within a specific shipping revenue range",
            component: (
              <MinMaxRevenueFilter
                minRevenue={dialogFilters.minShippingRevenue}
                maxRevenue={dialogFilters.maxShippingRevenue}
                onNumberOrNullChange={onNumberOrNullChange}
                maxKey={"maxShippingRevenue"}
                minKey={"minShippingRevenue"}
                minLabel={"Min shipping revenue"}
                maxLabel={"Max shipping revenue"}
              />
            ),
          },
        ],
      },
      {
        label: "Shipping Rates",
        settings: [
          {
            key: "shippingMethods",
            description:
              "Filter to orders where one of the selected shipping rates was used",
            component: (
              <IgFilterAutocomplete
                selectedValues={dialogFilters.shippingMethods}
                onChange={onStringArrFilterChange}
                filterKey={"shippingMethods"}
                allDataOptions={shippingMethodsData!}
                disableCloseOnSelect={true}
                getOptionDisabled={(option) => option === null}
              />
            ),
          },
        ],
        isSwitch: false,
        showOnPage: "all",
        config:
          currentExperience &&
          currentExperience.testTypes.hasTestShipping &&
          shippingMethodsData &&
          shippingMethodsData?.length > 0,
      },
      {
        label: "Z-Score",
        settings: [
          {
            key: "isZScoreEnabled",
            description: "Max Z-Score",
            component: (
              <ZScoreFilter
                isZScoreEnabled={dialogFilters.isZScoreEnabled}
                setDialogFilters={setDialogFilters}
                maxZScore={dialogFilters.maxZScore}
                onBooleanFilterChange={onBooleanFilterChange}
              />
            ),
          },
        ],
        isSwitch: true,
        showOnPage: "all",
      },
    ];
  }, [
    isTestProductsDefaultChange,
    dialogFilters,
    currentExperience,
    allCountryCodesData,
    shippingMethodsData,
    analyticsProducts?.length,
    selectedCustomEventIdentifiers,
    offerProducts?.length,
    hideTargetProductOption,
    isReadOnly,
  ]);

  const onFiltersClearAll = () => {
    const initialTestOrdersOnly =
      renderLocation === "sitewide"
        ? "sitewide_orders"
        : currentExperience && currentExperience.category === "personalization"
          ? "test_orders_only"
          : currentExperience?.experienceAnalysis.analyticsViewType ||
            "test_orders_only";

    dispatch(
      setDashboardFilter({
        ...dashboardFilters,
        ...initialDashboardFiltersState,
        analyticsViewType: initialTestOrdersOnly,
        maxZScore: maxZScoreConfig!.value,
      })
    );

    setDialogFilters((draft) => {
      draft = {
        ...initialDashboardFiltersState,
        analyticsViewType: initialTestOrdersOnly,
        maxZScore: maxZScoreConfig!.value,
      };

      return draft;
    });

    dispatch(closeDrawer("analyticsFilters"));
  };

  const onFiltersSave = () => {
    if (
      isTestProductsDefaultChange &&
      currentExperience &&
      dialogFilters.analyticsViewType !== "specific_products"
    ) {
      setIsTestProductsDefaultChange(false);
      mutateExperienceAnalysis.mutate({
        analyticsViewType: dialogFilters.analyticsViewType,
      });
    }

    const { startTime, endTime, ...rest } = [dialogFilters].map((filters) => {
      // Strip empty/untouched rows before persisting — they're UI-only defaults
      const activeLandingPageFilters = (
        filters.landingPageFilters ?? []
      ).filter(isActiveLandingPageFilter);

      return {
        ...filters,
        channels:
          filters.channels.length === getChannelTypeListCount(true)
            ? []
            : filters.channels,
        countryCodesFilterType: filters.countryCodes.length
          ? filters.countryCodesFilterType
          : "include",
        landingPageFilters: activeLandingPageFilters,
        // The drawer lifts the deprecated single-path filter into
        // landingPageFilters on open and offers no UI to set it, so it is always
        // cleared here — otherwise a value carried in from an older saved or
        // shared filter state would keep AND'ing onto the query invisibly.
        landingPage: EMPTY_LANDING_PAGE_SINGLE_FILTER,
      };
    })[0];
    const getTbActiveFilters = getTinybirdActiveAnalyticsFilters(
      {
        ...dashboardFilters,
        ...rest,
      },
      maxZScoreConfig!.value
    );
    if (Object.keys(getTbActiveFilters).length) {
      mpClickAnalyticsFilterSaveButton(
        getTbActiveFilters,
        getAnalyticsPageType(experience.category),
        pageName,
        calculateProductCategories(experience),
        officialCategoryName(experience.category, false),
        experience.id
      );
    }
    dispatch(setDashboardFilter({ ...dashboardFilters, ...rest }));
    dispatch(closeDrawer("analyticsFilters"));
  };

  return (
    <AnalyticsFiltersDrawer
      onFilterSave={onFiltersSave}
      openFilters={openFilters}
      onFiltersClearAll={onFiltersClearAll}
      filterButtons={filterButtons}
      isSaveLoading={mutateExperienceAnalysis.isPaused}
      pageName={pageName}
      isSaveDisabled={validationErrors[0] ?? { isDisabled: false, reason: "" }}
    />
  );
};
